<template>
  <div class="media-step-container" data-testid="oc-media-step">
    <div class="main-content">
      <div class="lead-page-title pb-10">{{ $t('order_creation.media_step.main_heading') }}</div>
      <div class="lead-page-muted">{{ $t('order_creation.media_step.description') }}</div>
      <hr class="section-divider" />

      <!-- For this order -->
      <section class="image-section">
        <div class="lead-page-title pb-20">{{ $t('order_creation.media_step.for_this_order') }}</div>
        <div class="image-grid">
          <div
            v-for="image in orderImagesComputed"
            :key="image.id"
            class="image-card"
            :class="{ selected: selectedImage?.id === image.id }"
            @click="selectImage(image)">
            <img :src="image.proxy.url_thumbnail" />
            <div class="image-overlay">
              <button class="delete-btn" @click.stop="removeOrderImage(image)">
                <IconTrash :size="16" />
              </button>
            </div>
          </div>
          <div
            class="upload-card dashed-border"
            :class="{ 'is-uploading': isUploading }"
            :aria-busy="isUploading"
            :aria-disabled="isUploading"
            @click="onUploadCardClick">
            <div class="upload-content">
              <div class="upload-icon">
                <div
                  v-if="isUploading"
                  class="spinner-border"
                  role="status"
                  :style="{ width: '28px', height: '28px', borderWidth: '2px' }"></div>
                <IconPhotoPlus v-else :size="48" stroke-width="1" />
              </div>
              <span>{{
                isUploading ? $t('order_creation.media_step.uploading') : $t('order_creation.media_step.upload_image')
              }}</span>
            </div>
          </div>
          <DropZone
            ref="dropzone"
            v-model="orderImages.images"
            :media-count="50"
            class="d-none"
            @on-file-select="activeUploads++"
            @extraction-complete="activeUploads = Math.max(0, activeUploads - 1)"
            @upload-error="activeUploads = Math.max(0, activeUploads - 1)" />
        </div>
      </section>

      <hr class="section-divider" />

      <!-- Imported from source -->
      <section
        v-if="isLoadingCrawledImages || crawledStatus !== CrawledImageStatus.NeverRequested"
        class="image-section">
        <div class="lead-page-title pb-10">{{ $t('order_creation.media_step.imported_from_source') }}</div>
        <div class="lead-page-muted pb-20">{{ $t('order_creation.media_step.imported_from_source_desc') }}</div>
        <div v-if="isLoadingCrawledImages || crawledStatus === CrawledImageStatus.Pending" class="imported-status">
          <div
            class="spinner-border"
            role="status"
            :style="{ width: '20px', height: '20px', borderWidth: '2px' }"></div>
          <span>{{ $t('order_creation.media_step.imported_from_source_loading') }}</span>
        </div>
        <div v-else-if="crawledStatus === CrawledImageStatus.Error" class="imported-status">
          <span>{{ $t('order_creation.media_step.imported_from_source_error') }}</span>
        </div>
        <div
          v-else-if="crawledStatus === CrawledImageStatus.Ready && importedImages.length === 0"
          class="imported-status">
          <span>{{ $t('order_creation.media_step.imported_from_source_empty') }}</span>
        </div>
        <div v-else class="image-grid">
          <div v-for="image in visibleImportedImages" :key="image.id" class="image-card" @click="addOrderImage(image)">
            <img :src="image.proxy.url_thumbnail" />
            <div class="image-overlay add-overlay">
              <IconPhotoPlus :size="30" class="mb-10" stroke-width="1.5" />
              {{ $t('order_creation.media_step.add') }}
            </div>
          </div>
        </div>
      </section>

      <!-- Company images -->
      <section class="image-section" v-if="availableCompanyImages?.length > 0">
        <div class="lead-page-title pb-10">{{ $t('order_creation.media_step.company_images') }}</div>
        <div class="lead-page-muted pb-20">{{ $t('order_creation.media_step.company_images_desc') }}</div>
        <div class="image-grid">
          <div v-for="image in availableCompanyImages" :key="image.id" class="image-card" @click="addOrderImage(image)">
            <img :src="image.proxy.url_thumbnail" />
            <div class="image-overlay add-overlay">
              <IconPhotoPlus :size="30" class="mb-10" stroke-width="1.5" />
              {{ $t('order_creation.media_step.add') }}
            </div>
          </div>
        </div>
      </section>

      <!-- AI Images -->
      <!-- TODO: uncomment when there is AI image generate endpoint -->
      <!-- <section class="image-section">
        <div class="d-flex flex-row align-items-center justify-content-between pb-10">
          <div class="lead-page-title">
            {{ $t('order_creation.media_step.ai_media') }}
          </div>
          <IconSparkles :size="24" class="ai-icon" stroke-width="2" />
        </div>
        <div class="lead-page-muted pb-20">{{ $t('order_creation.media_step.ai_media_desc') }}</div>
        <div class="d-flex flex-row align-items-center gap-2">
          <div class="image-grid">
            <div v-for="image in aiImages.filter(i => !i.selected)" :key="image.id" class="image-card"
              @click="addOrderImage(image)">
              <img :src="image.proxy.url_thumbnail" />
              <div class="image-overlay add-overlay">
                <IconPhotoPlus :size="30" class="mb-10" stroke-width="1.5" />
                {{ $t('order_creation.media_step.add') }}
              </div>
            </div>
          </div>
          <LeadButton variant="white">
            <template #prepend-icon>
              <IconRefresh size="20" stroke-width="1.5" />
            </template>
            {{ $t('order_creation.media_step.generate_more') }}
          </LeadButton>
        </div>
      </section> -->
    </div>

    <PreviewSectionComponent :image-to-preview="selectedImage" />
  </div>
</template>

<script lang="ts" setup>
import translator from '@/locale/translator';
import { useAutopilotWizardStep } from '@/composables/useAutopilotWizardStep';
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { useCrawledImagesStore } from '@/stores/crawledImages';
import { CrawledImageStatus } from '@/types/types';
import type { ImageObject, CrawledImage, CrawledImageStatusValue } from '@/types/types';
import { storeToRefs } from 'pinia';
// TODO: remove eslint rules once the ai media section is implemented
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { IconTrash, IconPhotoPlus, IconSparkles, IconRefresh } from '@tabler/icons-vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import LeadButton from '@/components/generic/LeadButton.vue';
import DropZone from '@/components/common/DropZone/DropZone.vue';
import PreviewSectionComponent from './subcomponents/PreviewSectionComponent.vue';
import { changeOrderImages } from '@/api/order';
import { useToast } from 'vue-toastification';

const toast = useToast();
const store = useOrderCreationStore();
const crawledImagesStore = useCrawledImagesStore();
const { orderImages, companyImages } = storeToRefs(store);

/* -------------------------------- AUTOPILOT ------------------------------- */
useAutopilotWizardStep(() => ({
  key: 'media',
  title: translator.get('order_creation.media_step.main_heading'),
  description:
    'The user picks the images used for the job ad and the ads: upload their own, reuse images imported from the source, or generate AI images.',
  on_screen: { selected_image_count: orderImages.value.images.length },
}));

const dropzone = ref<InstanceType<typeof DropZone> | null>(null);

const selectedImage = ref<ImageObject | null>(null);

// persists across polls: we keep ImageObject instances stable so `selected: true`
// set on a crawled card survives a refetch that returns the same image_id.
const crawledImagesById = ref<Map<number, ImageObject>>(new Map());
const crawledStatus = ref<CrawledImageStatusValue>(CrawledImageStatus.NeverRequested);

const importedImages = computed<ImageObject[]>(() => {
  return Array.from(crawledImagesById.value.values());
});

const aiImages = ref<ImageObject[]>([]);

function toImageObject(crawled: CrawledImage): ImageObject {
  // populate the fields the media step actually reads (id, hash, proxy, type, selected).
  // the rest are set to neutral defaults — they aren't used for crawled cards.
  return {
    id: crawled.image_id,
    status: 0,
    hash: crawled.hash,
    extension: '',
    width: crawled.width ?? 0,
    height: crawled.height ?? 0,
    bits: 0,
    size: 0,
    class: 0,
    description: crawled.alt,
    inscription: '',
    source: '',
    colors_popular: [],
    colors_representive: [],
    focus_x: null,
    focus_y: null,
    proxy_url: crawled.proxy_url,
    media_object_id: null,
    media_stock_purchase_id: null,
    created_at: '',
    modified_at: '',
    proxy: {
      url_raw: crawled.proxy_url,
      url_preview: crawled.proxy_url,
      url_thumbnail: crawled.proxy_url,
    },
    type: 'crawled',
    class_string: 'image',
    resource_id: crawled.resource_id,
  } as ImageObject;
}

function mergeCrawled(images: CrawledImage[]) {
  const incomingIds = new Set(images.map((i) => i.image_id));
  // drop images no longer returned (e.g. crawler re-ran)
  for (const id of Array.from(crawledImagesById.value.keys())) {
    if (!incomingIds.has(id)) crawledImagesById.value.delete(id);
  }
  // add new ones, keep existing ones (preserve `selected` state)
  for (const crawled of images) {
    if (!crawledImagesById.value.has(crawled.image_id)) {
      crawledImagesById.value.set(crawled.image_id, toImageObject(crawled));
    }
  }
}

const orderImagesComputed = computed(() => {
  return [...orderImages.value.images, ...importedImages.value, ...companyImages.value, ...aiImages.value].filter(
    (img) => img.selected
  );
});

const availableCompanyImages = computed(() => {
  return companyImages.value.filter((img) => {
    if (img.selected) return false;
    return !orderImagesComputed.value.some((oi) => oi.hash === img.hash);
  });
});

const visibleImportedImages = computed<ImageObject[]>(() => {
  return importedImages.value.filter((img) => {
    if (img.selected) return false;
    return !orderImagesComputed.value.some((oi) => oi.hash === img.hash);
  });
});

const activeUploads = ref(0);
const isUploading = computed(() => activeUploads.value > 0);
const isLoadingCrawledImages = ref(true);

function triggerFileUpload() {
  dropzone.value?.openInput();
}

function onUploadCardClick() {
  if (isUploading.value) return;
  triggerFileUpload();
}

function selectImage(image: ImageObject) {
  selectedImage.value = image;
}

function addOrderImage(image: ImageObject) {
  image.selected = true;
  selectedImage.value = image;
}

function removeOrderImage(image: ImageObject) {
  image.selected = false;
  if (selectedImage.value?.id === image.id) {
    const remaining = orderImagesComputed.value.filter((img) => img.id !== image.id);
    selectedImage.value = remaining.length > 0 ? remaining[0] : null;
  }
}

async function refreshCrawledImages() {
  if (!store.order.id) return;
  const entry = await crawledImagesStore.fetchForOrder(store.order.id);
  crawledStatus.value = entry.status;
  mergeCrawled(entry.images);
}

onMounted(async () => {
  try {
    if (store.order.id) {
      await store.getOrderById(store.order.id);
    }
    store.fetchCompanyImagesForCurrentOrder();

    const orderId = store.order.id;
    if (orderId) {
      await refreshCrawledImages();
      if (crawledStatus.value === CrawledImageStatus.Pending) {
        crawledImagesStore.startPolling(orderId);
        const unwatch = watch(
          () => crawledImagesStore.getEntry(orderId),
          (entry) => {
            if (!entry) return;
            crawledStatus.value = entry.status;
            mergeCrawled(entry.images);
          },
          { deep: true }
        );
        onBeforeUnmount(unwatch);
      }
    }

    if (orderImagesComputed.value.length > 0) {
      selectedImage.value = orderImagesComputed.value[0];
    } else {
      const firstAvailableImage = [
        ...orderImages.value.images,
        ...importedImages.value,
        ...companyImages.value,
        ...aiImages.value,
      ].find((img) => !img.selected);

      if (firstAvailableImage) {
        selectedImage.value = firstAvailableImage;
      }
    }
  } finally {
    isLoadingCrawledImages.value = false;
  }
});

onBeforeUnmount(() => {
  if (store.order.id) {
    crawledImagesStore.stopPolling(store.order.id);
  }
});

watch(
  () => orderImages.value.images.length,
  (newLength, oldLength) => {
    if (newLength > (oldLength || 0)) {
      const newImage = orderImages.value.images[newLength - 1];
      if (newImage) {
        selectedImage.value = newImage;
      }
    }
  }
);

async function saveMediaImages() {
  if (!store.order.id) return true;

  const allImages = [...orderImages.value.images, ...importedImages.value, ...companyImages.value, ...aiImages.value];

  const media = {
    companyLogo: orderImages.value.companyLogo,
    images: allImages,
  };

  try {
    await changeOrderImages(store.order.id, media, {
      filterSelected: true,
      setDefaultCompanyLogo: store.setDefaultCompanyLogo,
    });
    return true;
  } catch {
    toast.error('Failed to attach media to the order');
    return false;
  }
}

defineExpose({
  saveMediaImages,
});
</script>
<style scoped lang="scss">
.media-step-container {
  background-color: #fff;
  display: flex;
  min-height: 100%;
  font-family: var(--font-brand);
  flex-grow: 1;
}

.main-content {
  flex-grow: 1;
  max-width: 659px;
  border-right: 1px solid #eaecf0;
  padding: 30px;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
}

.step-subtitle {
  font-size: 14px;
  color: #475467;
  margin-bottom: 24px;
}

.image-section {
  margin-bottom: 32px;
}

.ai-icon {
  color: var(--tblr-primary);
}

.section-subtitle {
  font-size: 14px;
  color: #475467;
  margin-bottom: 16px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 193px);
  gap: 8px;
}

.imported-status {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #8e939d;
  font-size: 13px;
}

.image-card {
  position: relative;
  width: 193px;
  height: 120px;
  aspect-ratio: 193 / 120;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #eaecf0;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &.selected {
    border-color: var(--tblr-primary);
    box-shadow: 0 0 0 2px var(--tblr-primary);
  }
  background-image:
    linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
  background-size: 20px 20px;
  background-position:
    0 0,
    0 10px,
    10px -10px,
    -10px 0px;
  background-color: #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.2s;
    display: flex;
    justify-content: flex-end;
    color: #000;
    font-size: 13px;
    font-weight: 600;

    &.add-overlay {
      flex-direction: column;
      background: #ffffffcc;
      justify-content: center;
      align-items: center;
    }
  }

  &:hover .image-overlay {
    opacity: 1;
  }

  .delete-btn {
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0px 0px 4px 0px #00000040;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    justify-self: flex-end;
    margin-top: 4px;
    margin-right: 4px;
  }
}

.dashed-border {
  width: 100%;
  height: 120px;
  padding: 10px;
  border-radius: 10px;

  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='%23EAECF0' stroke-width='7' stroke-dasharray='6%2c 6'/%3e%3c/svg%3e");
}

.upload-card {
  width: 193px;
  height: 120px;
  aspect-ratio: 193 / 120;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s,
    opacity 0.2s;

  &:hover {
    border-color: #cfd2d6;
  }

  &.is-uploading {
    cursor: progress;
    pointer-events: none;
    opacity: 0.85;
  }

  .upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #8e939d;
    font-size: 12px;
    font-weight: 400;
  }

  .upload-icon {
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8e939d;
  }

  &.add-card {
    background-color: #f2f4f7;

    .upload-content {
      color: #344054;
    }

    .upload-icon {
      background: none;
    }
  }
}

.section-divider {
  background: #eaecf0;
  margin: 30px -30px;
}

.pb-10 {
  padding-bottom: 10px;
}

.pb-20 {
  padding-bottom: 20px;
}

.mb-10 {
  margin-bottom: 10px;
}
</style>
